import { Component, type ComponentChildren } from "preact";

type Props = { children: ComponentChildren; label: string };
type State = { error: Error | null };

/**
 * A silent blank area is worse than a visible error — the workspace-wide HOLD list keeps
 * "don't silently fail" even under the 2026-08-11 usable-first policy change (only speed,
 * completeness, and measured correctness were relaxed). Without this, a thrown render error
 * (most plausibly the lazily-loaded IFC chunk failing to fetch after a redeploy changed its
 * hash) unmounts the nearest ancestor with no explanation. Preact error boundaries are class
 * components — there is no hook equivalent, same as React.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: unknown): State {
    return { error: error instanceof Error ? error : new Error(String(error)) };
  }

  componentDidCatch(error: unknown) {
    // eslint-disable-next-line no-console
    console.error(`[ErrorBoundary:${this.props.label}]`, error);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="model-fallback error-boundary" role="alert">
          <strong>{this.props.label} failed to load</strong>
          <p>{this.state.error.message}</p>
          <p className="error-boundary-hint">If this app was updated since you opened this tab, a full reload usually fixes it.</p>
          <button type="button" onClick={() => window.location.reload()}>Reload page</button>
        </div>
      );
    }
    return this.props.children;
  }
}
